import _ from "lodash";
import moment from "moment";
import qs from "qs";
import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { getAccessDevicesAsync } from "src/api/access/productac-api";
import { Ac2AccessDeviceModel } from "src/api/access/productac-types";
import { getBuildingAsync, getBuildingsAsync } from "src/api/building/building-api";
import { BuildingListParams, BuildingModel, CommonFacilityModel } from "src/api/building/building-types";
import { getCctvDeviceListAsync } from "src/api/cctv/cctv-api";
import { CctvDevice } from "src/api/cctv/cctv-types";
import { useApiOperation } from "src/api/hooks";
import { getBuildingDeviceAsync } from "src/api/iot/iot-api";
import { BuildingLocation, DeviceModel, UnionBuildingLocation } from "src/api/iot/iot-types";
import { BaseCheckbox } from "src/components";
import { YmdFormat } from "src/utils/common-util";
import { SelectOption } from "../building/building-types";
import BuildingSelect from "./components/BuildingSelect";
import MonitoringTable from "./components/MonitoringTable";
import { DeviceType, ExecuteResultType, MonitoringType } from "./monitoring-types";

type AccessDevicePassData = {
  location: UnionBuildingLocation;
  floorValue: string;
};

/**
 * 건물 상세
 * iot 목록 가져오기
 * control 호출
 * 실행
 */

type AccessDeviceType = Ac2AccessDeviceModel & { isLoading?: boolean };

export type CctvDeviceType = CctvDevice & { isLoading?: boolean };

const TestSpaceMonitoring = () => {
  const intervalRef = useRef<NodeJS.Timer>();
  const location = useLocation();
  const [updateTime, setUpdateTime] = useState("");
  const [building, setBuilding] = useState<BuildingModel>(); // 건물 상세
  const [buildingOptions, setBuildingOptions] = useState<SelectOption[]>([]); // 건물 선택 옵션
  const [hasDevices, setHasDevices] = useState(true); //  데이터 있는 공간 체크박스

  const [monitoringList, setMonitoringList] = useState<MonitoringType[]>([]); //iot 기기 데이터
  const [iotDevices, setIotDevices] = useState<Array<DeviceModel & { isLoading?: boolean }>>(); // 출입기기 데이터
  const [accessGroup, setAccessGroup] = useState<Array<AccessDeviceType & { isLoading?: boolean }>>(); // 출입기기 데이터
  const [cctvDevices, setCctvDevices] = useState<CctvDeviceType[]>([]);
  const [apisLoaded, setApisLoaded] = useState(false); // API 로딩 상태를 추적하는 변수
  // buildingId - queryParams 적용
  const queryParams = useMemo(
    () => qs.parse(location.search, { ignoreQueryPrefix: true, allowDots: true, decoder: (value) => decodeURIComponent(value) }),
    [location.search],
  );

  const { executeAsync: getBuildingList } = useApiOperation(getBuildingsAsync); // 건물 목록
  const { executeAsync: getBuilding } = useApiOperation(getBuildingAsync); // 건물 상세
  const { executeAsync: getAccessDevices } = useApiOperation(getAccessDevicesAsync, {
    noErrorModal: true,
    // noLoading: true,
  }); // 출입기기 목록
  const { executeAsync: getBuildingIotDevice } = useApiOperation(getBuildingDeviceAsync, {
    noErrorModal: true,
    noLoading: true,
  }); // 건물 IoT 기기 탭 목록
  const { executeAsync: getCctvDeviceList } = useApiOperation(getCctvDeviceListAsync, {
    noErrorModal: true,
    noLoading: true,
  }); // cctv api

  // 1. 건물 목록 가져오기
  useEffect(() => {
    const fetchAndStartInterval = async () => {
      const requestParams: BuildingListParams = {
        page: 0,
        size: 200,
        sort: { orders: [{ direction: "ASC", property: "buildingName" }] },
      };
      const { data } = await getBuildingList(requestParams);
      const result = data.data.content;

      setBuildingOptions(
        result.map((item) => ({
          value: String(item.id),
          label: item.buildingName,
        })),
      );

      const buildingId = queryParams.buildingId ? Number(queryParams.buildingId) : Number(result[0]?.id);
      if (!buildingId) return;

      try {
        await getBuildingDetail(buildingId);
      } finally {
        // 상세 조회가 실패해도 자동 갱신은 시작한다. 다음 주기에 다시 시도된다
        startInterval(buildingId);
      }
    };

    fetchAndStartInterval();

    // Cleanup: 컴포넌트 언마운트 시 인터벌 제거
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
      setUpdateTime("");
    };
  }, [queryParams.buildingId]);

  // cctv 목록 가져오기
  const fetchCctvDeviceList = async (buildingId: string) => {
    const { data } = await getCctvDeviceList({
      buildingId,
      sort: { orders: [{ direction: "ASC", property: "name" }] },
    });
    if (data.data.content) {
      const parsedCctvDevices = data.data.content.map((device) => {
        // cctvEventDataList가 있는 경우에만 처리
        if (device.cctvEventDataList) {
          const parsedEventList = device.cctvEventDataList.map((eventData) => {
            try {
              if (eventData.eventData && typeof eventData.eventData === "string") {
                return {
                  ...eventData,
                  eventData: JSON.parse(eventData.eventData),
                };
              }
              return eventData;
            } catch (error) {
              return eventData;
            }
          });

          return {
            ...device,
            cctvEventDataList: parsedEventList,
            isLoading: true,
          };
        }

        return {
          ...device,
          isLoading: true,
        };
      });

      setCctvDevices(parsedCctvDevices);
    }
    return data;
  };

  // 2. 건물 상세 가져오기 > 출입기기 목록 + 건물 iot 목록 + cctv 목록 호출
  const getBuildingDetail = async (buildingId: number) => {
    setUpdateTime("");
    setApisLoaded(false); // API 로딩 시작 전에 상태 초기화
    const { data } = await getBuilding({ buildingId });
    const buildingResult = data.data.content.building;
    if (buildingResult) {
      setBuilding(buildingResult);
      const monitoringListResult = handleSetMonitoringList(buildingResult);
      setMonitoringList(monitoringListResult);

      if (monitoringListResult.length > 0) {
        // 하나가 실패해도 나머지 기기로 표시한다
        Promise.all([
          getAccessGroupList().catch(() => undefined),
          getBuildingIotList(String(buildingResult?.id)).catch(() => undefined),
          fetchCctvDeviceList(String(buildingResult?.id)).catch(() => undefined),
        ]).then(() => {
          setApisLoaded(true); // 모든 API 호출이 완료되면 상태 업데이트
          const date = moment().format(YmdFormat.YYYY_MM_DD_HH_MM);
          setUpdateTime(date);
        });
      }
    }
  };

  // 출입기기 목록 불러오기
  const getAccessGroupList = async () => {
    const { data } = await getAccessDevices({ includeDeviceStatusInfo: true, cacheable: true });

    const access = data.data.content.map((item) => ({ ...item, isLoading: true }));
    setAccessGroup(access);
  };

  // 건물에 등록된 iot 목록 가져오기 >
  const getBuildingIotList = async (buildingId: string) => {
    const { data: deviceData } = await getBuildingIotDevice({
      buildingId,
      sort: { orders: [{ direction: "ASC", property: "internalDisplayName" }] },
    });
    if (deviceData.data) {
      if (deviceData.data.content.length === 0) {
        setUpdateTime("");
      }
      const devicesResult = deviceData.data.content;

      setIotDevices(devicesResult);
    }
    return deviceData;
  };

  //
  // 모니터링 테이블에 보여지는 최종 데이터 구조로
  const veiwTableData = useMemo(() => {
    if (monitoringList.length > 0 && apisLoaded) {
      // 모니터링 데이터에 출입기기와 CCTV 장치를 한번에 처리
      const clonedMonitoringList = _.cloneDeep(monitoringList);
      let accessDevices: AccessDeviceType[] = [];
      let cctvDeviceList: CctvDeviceType[] = [];
      let accessDeviceLocation = "";

      const controlDevices: ExecuteResultType[] = [];
      //iot 기기 제어 포맷
      const iotControlFormat: ExecuteResultType[] =
        iotDevices?.map((device) => ({
          buildingDeviceId: device.id,
          location: device.building.location,
          labels: device.labels,
          platform: device.platformDevice.platformAccount.platform,
          labelType: null,
          internalDisplayName: device.internalDisplayName || "",
          result: null,
          isLoading: true,
        })) || [];

      iotControlFormat.forEach((device) => {
        const hasLabel = (name: string, value: string) => device.labels?.some((label) => label.name === name && label.value === value);
        if (hasLabel("용도", DeviceType.SENSOR)) {
          controlDevices.push({ ...device, labelType: DeviceType.SENSOR });
        }
        if (hasLabel("용도", "공조")) {
          controlDevices.push({ ...device, labelType: DeviceType.INDOOR_UNIT });
        }
        if (hasLabel("용도", DeviceType.TEMPERATURE)) {
          controlDevices.push({ ...device, labelType: DeviceType.TEMPERATURE });
        }
        if (hasLabel("용도", DeviceType.LIGHT)) {
          controlDevices.push({ ...device, labelType: DeviceType.LIGHT });
        }
        if (hasLabel("용도", "화장실")) {
          if (hasLabel("성별", "남")) {
            controlDevices.push({ ...device, labelType: DeviceType.MENS_TOILET });
          }
          if (hasLabel("성별", "여")) {
            controlDevices.push({ ...device, labelType: DeviceType.WOMEN_TOILET });
          }
        }
      });

      const monitoringDevices = clonedMonitoringList.map((item) => {
        const floorId = item.floorId;
        const floorDevices = item.floorDevices;

        //
        controlDevices.forEach((controlDevice) => {
          const category = controlDevice.location.category;
          const identifier = controlDevice.location.identifier;
          const labelType = controlDevice.labelType;

          if (category === BuildingLocation.FLOOR && String(floorId) === String(identifier)) {
            if (labelType === DeviceType.SENSOR) {
              floorDevices.sensor.push(controlDevice);
            } else if (labelType === DeviceType.INDOOR_UNIT) {
              floorDevices.indoorUnit.push(controlDevice);
            } else if (labelType === DeviceType.TEMPERATURE) {
              floorDevices.temperature.push(controlDevice);
            } else if (labelType === DeviceType.LIGHT) {
              floorDevices.light.push(controlDevice);
            } else if (labelType === DeviceType.MENS_TOILET) {
              floorDevices.mensToilet.push(controlDevice);
            } else if (labelType === DeviceType.WOMEN_TOILET) {
              floorDevices.womensToilet.push(controlDevice);
            }
          } else if (category === BuildingLocation.FACILITY) {
            const facility = item.facilityList.find((facility) => facility.id === identifier);
            if (facility) {
              const facilityDevices = facility?.facilityDevices;
              if (labelType === DeviceType.SENSOR) {
                facilityDevices.sensor.push(controlDevice);
              } else if (labelType === DeviceType.INDOOR_UNIT) {
                facilityDevices.indoorUnit.push(controlDevice);
              } else if (labelType === DeviceType.TEMPERATURE) {
                facilityDevices.temperature.push(controlDevice);
              } else if (labelType === DeviceType.LIGHT) {
                facilityDevices.light.push(controlDevice);
              } else if (labelType === DeviceType.MENS_TOILET) {
                facilityDevices.mensToilet.push(controlDevice);
              } else if (labelType === DeviceType.WOMEN_TOILET) {
                facilityDevices.womensToilet.push(controlDevice);
              }
            }
          }
        });

        if (accessGroup && accessGroup.length > 0) {
          const accessLocation: AccessDevicePassData = {
            location: BuildingLocation.FLOOR, // 층
            floorValue: item.floorValue, // 층 값
          };
          accessDeviceLocation = //COURT5333.3F 와 같은 값
            accessLocation.location === BuildingLocation.FLOOR ? `${building?.locationCode}.${accessLocation.floorValue}` : accessLocation.floorValue;
          //출입기기 장치 처리
          accessDevices = accessGroup?.filter((accessItem) => accessItem.deviceLabels?.some((label) => label.value === accessDeviceLocation)) || [];
        }

        // CCTV 장치 처리
        if (cctvDevices && cctvDevices.length > 0) {
          cctvDeviceList = cctvDevices?.filter(({ cctvDevicePartnerList }) =>
            cctvDevicePartnerList?.some((cctvItem) => String(cctvItem.buildingFloorId) === String(item.floorId)),
          );
        }

        // 두 장치 정보를 포함한 객체 반환
        return {
          ...item,
          floorDevices: {
            ...item.floorDevices,
            access: accessDeviceLocation.includes(item.floorValue) ? accessDevices : [],
            cctv: cctvDeviceList || [],
          },
        };
      });
      return monitoringDevices;
    }

    // 기본값으로 원본 monitoringList 반환
    return monitoringList;
  }, [monitoringList, accessGroup?.length, cctvDevices?.length, iotDevices?.length, building?.locationCode, apisLoaded]);

  // 1. 건물의 층별 데이터 구성

  //
  const handleSetMonitoringList = (buildingData: BuildingModel) => {
    const floorList =
      buildingData.buildingFloorList
        ?.sort((a, b) => Number(b?.floorNum) - Number(a?.floorNum))
        .map((floor) => {
          const roomIds = floor.buildingRoomList?.map((item) => String(item.id)) || [];
          const floorValue = String(floor.floorNum).includes("-") ? `${floor.floorNum?.toString().replace("-", "B")}` : `${floor.floorNum}F`;
          const floorId = String(floor.id);

          return {
            floorId,
            roomIds,
            floorNum: Number(floor.floorNum),
            floorValue,
            floorDevices: {
              access: [] as Ac2AccessDeviceModel[],
              sensor: [] as ExecuteResultType[],
              indoorUnit: [] as ExecuteResultType[],
              temperature: [] as ExecuteResultType[],
              light: [] as ExecuteResultType[],
              mensToilet: [] as ExecuteResultType[],
              womensToilet: [] as ExecuteResultType[],
              cctv: [] as CctvDeviceType[],
            },
          };
        }) || [];

    // 2. 건물 공용공간 데이터 구성 (회의실, 좌석, 편의시설)
    const meetingRooms = spaceSorting(buildingData.buildingCommonFacility?.meetingRoomList || []);
    const deskList = buildingData.buildingCommonFacility?.deskSpace?.deskGroupList?.flatMap((item) => item.deskList) || [];
    const sortedDeskList = deskList.length > 0 ? spaceSorting(deskList as CommonFacilityModel[]) : [];
    const refreshRooms = spaceSorting(buildingData.buildingCommonFacility?.refreshRoomList || []);

    // 3. 모든 공용공간 통합
    const commonFacility = [...meetingRooms, ...sortedDeskList, ...refreshRooms].map((facility) => {
      const floorValue = facility?.isGround ? `${facility?.floorNum}F` : `B${facility?.floorNum}` || "";

      return {
        id: String(facility?.id || ""),
        facilityName: facility?.facilityName || "",
        floorValue,
        facilityDevices: {
          access: [] as Ac2AccessDeviceModel[],
          sensor: [] as ExecuteResultType[],
          indoorUnit: [] as ExecuteResultType[],
          temperature: [] as ExecuteResultType[],
          light: [] as ExecuteResultType[],
          mensToilet: [] as ExecuteResultType[],
          womensToilet: [] as ExecuteResultType[],
          cctv: [] as CctvDeviceType[],
        },
      };
    });

    // 4. 층별로 해당 층의 공용공간 할당
    const monitoringListResult: MonitoringType[] = floorList.map((floor) => ({
      ...floor,
      facilityList: commonFacility.filter((facility) => facility.floorValue === floor.floorValue),
    }));

    return monitoringListResult as MonitoringType[];
  };

  // 모니터링 테이블에 보여지는 데이터 구조로 변경

  // 새로고침 버튼 클릭 시 호출
  const handleRefresh = async (buildingId: number) => {
    if (buildingId) {
      setApisLoaded(false); // 새로고침 시에도 API 로딩 상태 초기화
      try {
        await getBuildingDetail(buildingId);
      } finally {
        // 새로고침 후 인터벌 재설정. 상세 조회가 실패해도 자동 갱신은 이어간다
        startInterval(buildingId);
      }
    }
  };

  // 인터벌 시작 및 재시작 로직
  const startInterval = (buildingId?: number) => {
    let id = queryParams.buildingId ? Number(queryParams.buildingId) : buildingId || Number(building?.id);
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }
    intervalRef.current = setInterval(() => {
      getBuildingDetail(id);
    }, 60000);
  };

  const spaceSorting = (list: CommonFacilityModel[]) => {
    return list.sort((a, b) => {
      if (a?.facilityName && b?.facilityName) {
        if (a!.facilityName > b!.facilityName) return 1;
        if (b!.facilityName > a!.facilityName) return -1;
      }
      return 0;
    });
  };

  // 디바이스 존재 여부에 따라 모니터링 데이터 필터링하는 함수 (데이터 있는 공간만 보기)
  const filterMonitoringByDevices = (data: MonitoringType[], filterEnabled: boolean): MonitoringType[] => {
    if (!data.length) return [];
    if (!filterEnabled) return data;

    return data
      .map((floor) => {
        // 층에 디바이스가 있는지 체크
        const hasFloorDevices = Object.values(floor.floorDevices).some((device) => Array.isArray(device) && device.length > 0);

        // 시설들 중에서 디바이스가 있는 시설만 필터링
        const facilitiesWithDevices = floor.facilityList.filter((facility) =>
          Object.values(facility.facilityDevices).some((device) => Array.isArray(device) && device.length > 0),
        );

        // 층에 디바이스가 있고, 시설에는 디바이스가 없는 경우
        if (hasFloorDevices && facilitiesWithDevices.length === 0) {
          return {
            ...floor,
            facilityList: [], // 시설 목록을 비움
          };
        }

        // 층에 디바이스가 없고, 시설에만 디바이스가 있는 경우
        if (!hasFloorDevices && facilitiesWithDevices.length > 0) {
          return {
            ...floor,
            facilityList: facilitiesWithDevices, // 디바이스가 있는 시설만 포함
          };
        }

        // 둘 다 디바이스가 있는 경우
        if (hasFloorDevices && facilitiesWithDevices.length > 0) {
          return {
            ...floor,
            facilityList: facilitiesWithDevices,
          };
        }

        return null; // 둘 다 디바이스가 없는 경우
      })
      .filter(Boolean) as MonitoringType[]; // null 값 제거
  };

  return (
    <div className="page-monitoring" style={{ width: 980, maxWidth: 980 }}>
      <div className="contents-container__table">
        <div className="contents-container__search-wrap">
          <div className="left-area">
            <div className="flex-center">
              <BuildingSelect building={building || undefined} buildingOptions={buildingOptions} getBuildingDetail={getBuildingDetail} />
              <BaseCheckbox
                className="mb8"
                id={"monitoringData"}
                name={"monitoringData"}
                label="데이터 있는 공간만 보기"
                onChange={setHasDevices}
                checked={hasDevices}
              />
            </div>
          </div>
        </div>
        <div className="px30">
          <MonitoringTable
            monitoring={filterMonitoringByDevices(veiwTableData || [], hasDevices)}
            handleRefresh={handleRefresh}
            buildingId={Number(building?.id)}
            date={updateTime}
          />
        </div>
      </div>
    </div>
  );
};

export default TestSpaceMonitoring;
